import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import React, { lazy, Suspense, useEffect } from 'react';
import GoogleTagManager, {
  GoogleTagManagerNoScript,
} from './components/seo/GoogleTagManager';
import MicrosoftClarity from './components/seo/MicrosoftClarity';
import MetaPixel from './components/seo/MetaPixel';
import GoogleAdsConversion from './components/seo/GoogleAdsConversion';
import { Analytics } from '@vercel/analytics/react';
import './index.css';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { UserProvider } from './lib/context/userContext';
import { trackPageView } from '@/lib/services/analyticsService';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SkipLink from '@/components/layout/SkipLink';
import VapiLoader from '@/components/VapiLoader';
import ErrorBoundary from '@/components/ErrorBoundary';
import RootLayout from '@/components/layout/RootLayout';
import ScrollProgress from '@/components/ui/ScrollProgress';
import ExitIntentOverlay from '@/components/ui/ExitIntentOverlay';
import StickyMobileCTA from '@/components/ui/StickyMobileCTA';
import AdminRoute from '@/components/admin/AdminRoute';
import { isFinanceDocument } from '@/lib/financePrivacy';
import GlobalClickTracking from '@/components/analytics/GlobalClickTracking';

// Replace static imports with lazy loaded components
const Index = lazy(() => import('./pages/Index'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AgentsPage = lazy(() => import('./pages/services/Agents'));
const AdsPage = lazy(() => import('./pages/services/Ads'));
const AutomationPage = lazy(() => import('./pages/services/Automation'));
const WebsitePage = lazy(() => import('./pages/services/Website'));
const GoogleAdsForContractorsPage = lazy(
  () => import('./pages/services/GoogleAdsForContractors')
);
const AboutPage = lazy(() => import('./pages/About'));
const AboutMaxKelleyPage = lazy(() => import('./pages/AboutMaxKelley'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const NewsletterConfirmedPage = lazy(
  () => import('./pages/NewsletterConfirmedPage')
);
const VoicemailPage = lazy(() => import('./pages/Voicemail'));
const LandscaperPage = lazy(() => import('./pages/Landscaper'));
const WebDesignPage = lazy(() => import('./pages/WebDesign'));
const ReviewStandsPage = lazy(() => import('./pages/ReviewStands'));
const ReviewStandsThankYouPage = lazy(
  () => import('./pages/ReviewStandsThankYou')
);
const ReviewStandsSetupPage = lazy(() => import('./pages/ReviewStandsSetup'));
const OpsOrdersPage = lazy(() => import('./pages/ops/OpsOrders'));
const OpsOrderPage = lazy(() => import('./pages/ops/OpsOrder'));
const PortalLandingPage = lazy(() => import('./pages/portal/PortalLanding'));
const PortalClientPage = lazy(() => import('./pages/portal/PortalClient'));
const PortalAdminPage = lazy(() => import('./pages/portal/PortalAdmin'));
const AIVoicemailDemo = lazy(() => import('./pages/AIVoicemailDemo'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ROICalculator = lazy(() => import('./pages/tools/ROICalculator'));
const SiteAudit = lazy(() => import('./pages/tools/SiteAudit'));
const FourSeasonsIntake = lazy(() => import('./pages/intake/FourSeasons'));
const NEConstructionEdIntake = lazy(
  () => import('./pages/intake/NEConstructionEd')
);
const MattFarahIntake = lazy(() => import('./pages/intake/MattFarah'));
const WebhooksDocumentationPage = lazy(
  () => import('./pages/developer/webhooks')
);
const ApiReferencePage = lazy(() => import('./pages/developer/api-reference'));
const GlassmorphicBackground = lazy(
  () => import('./components/ui/GlassmorphicBackground')
);
const ChatWidget = lazy(() => import('./components/chat/ChatWidget'));

// Blog pages
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));

// E-E-A-T and IA pages
const TeamPage = lazy(() => import('./pages/Team'));
const AuthorPage = lazy(() => import('./pages/authors/AuthorPage'));
const CaseStudyIndex = lazy(
  () => import('./pages/case-studies/CaseStudyIndex')
);
const CaseStudyPage = lazy(() => import('./pages/case-studies/CaseStudyPage'));
const LocationsPage = lazy(() => import('./pages/locations/Locations'));
const FaqPage = lazy(() => import('./pages/Faq'));

// Industry hub pages
const IndustriesPage = lazy(() => import('./pages/industries/Industries'));
const IndustryDetail = lazy(() => import('./pages/industries/IndustryDetail'));

// Admin pages
const AdminLoginPage = lazy(() => import('./pages/admin/LoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const FinancePage = lazy(() => import('./pages/admin/FinancePage'));
const BlogPostForm = lazy(() => import('./pages/admin/BlogPostForm'));
const TestimonialsPage = lazy(() => import('./pages/admin/TestimonialsPage'));
const ActivityLogsPage = lazy(() => import('./pages/admin/ActivityLogsPage'));
const LeadInboxPage = lazy(() => import('./pages/admin/LeadInboxPage'));
const BlogPostsPage = lazy(() => import('./pages/admin/BlogPostsPage'));
const BlogCategoriesPage = lazy(
  () => import('./pages/admin/BlogCategoriesPage')
);
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'));
const ApiKeysPage = lazy(() => import('./pages/admin/api-keys'));
const AIWriterPage = lazy(() => import('./pages/admin/AIWriterPage'));
const SeoAuditPage = lazy(() => import('./pages/admin/SeoAuditPage'));
const AgentHealthPage = lazy(() => import('./pages/admin/AgentHealthPage'));

// True when rendered by react-snap's puppeteer crawler. We suppress loading
// spinners under this flag so the snapshot captures real page content instead
// of a "Loading..." state that would become the indexed HTML.
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

// Loading fallback component
const PageLoader = () => {
  if (IS_PRERENDERING) return null;
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
    >
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-t-elev8-blue border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-lg font-medium">Loading...</p>
      </div>
    </div>
  );
};

// Error element component
const ErrorElement = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
  >
    <div className="flex flex-col items-center text-center max-w-md px-4">
      <h1 className="text-3xl font-bold mb-4">Something went wrong</h1>
      <p style={{ color: 'var(--text-secondary)' }} className="mb-6">
        We couldn't load this page. Please try again or return to the homepage.
      </p>
      <a href="/" className="btn-primary px-6 py-3">
        Return to Homepage
      </a>
    </div>
  </div>
);

// Layout component that wraps the main site
const Layout = () => {
  const pathname = useLocation().pathname;
  const isReviewStore = pathname === '/review-stands';
  const isContactPage = pathname === '/contact';
  // Apply theme class to document element when the component mounts
  useEffect(() => {
    // Log for debugging purposes
    if (import.meta.env.DEV) {
      console.log('Layout mounted');
      console.log('Current pathname:', window.location.pathname);
      console.log('GlassmorphicBackground should be rendering...');
    }
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col">
      <SkipLink />

      {/* Scroll progress indicator */}
      <ScrollProgress />

      {/* Global 3D spheres background for all main site pages */}
      <React.Suspense fallback={null}>
        {!isReviewStore && !isContactPage && <GlassmorphicBackground />}
      </React.Suspense>

      {/* Consistent navbar for all non-admin pages */}
      <ErrorBoundary fallback={<div className="h-20 w-full"></div>}>
        {!isReviewStore && <Navbar />}
      </ErrorBoundary>

      <Suspense fallback={<PageLoader />}>
        <main id="main-content" className="flex-grow z-10 pt-16">
          <Outlet />
        </main>
      </Suspense>

      <Footer />

      {/* AI Chat Widget */}
      <React.Suspense fallback={null}>
        {!isReviewStore && !isContactPage && <ChatWidget />}
      </React.Suspense>

      {/* Exit-intent overlay for lead capture */}
      {!isReviewStore && !isContactPage && <ExitIntentOverlay />}

      {/* Sticky mobile CTA bar */}
      {!isReviewStore && !isContactPage && <StickyMobileCTA />}

      {/* DataLayer coverage for tel/mailto/contact-form anchor clicks */}
      <GlobalClickTracking />

      {/* Vapi AI widget — lazy-loaded after first paint, skipped under prerender */}
      <VapiLoader disabled={isReviewStore || isContactPage} />
    </div>
  );
};

// Create QueryClient outside component to prevent recreation on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Get analytics IDs from environment variables
const GTM_ID = import.meta.env.VITE_GTM_ID || '';
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID || '';
// Meta (Facebook/Instagram) Pixel. A Pixel ID is public (it ships to every
// visitor's browser), so it's safe to hardcode as the default; can still be
// overridden per-environment via VITE_META_PIXEL_ID (comma-separated for more
// than one).
//
// Two pixels on purpose, for the duration of the migration:
//   1599468397323051 — "ELEV8 Growth Solutions", the dataset in the Elev8
//     Growth business portfolio (460704199912194). New campaigns attribute to
//     this one, so it needs to start building history now.
//   836749966761636  — the long-standing pixel. Kept firing so the existing
//     history, custom audiences and any live campaigns don't go dark while the
//     new dataset is being confirmed.
//
// Drop 836749966761636 once 1599468397323051 is confirmed receiving events in
// Events Manager AND assigned to the ad account running the campaigns.
const META_PIXEL_ID =
  import.meta.env.VITE_META_PIXEL_ID || '1599468397323051,836749966761636';
// Google Ads conversion tracking ID (public — ships to the browser). The "Lead"
// conversion event is fired from trackGenerateLead(); overridable via env.
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID || 'AW-17422177310';

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <RootLayout />,
      errorElement: <ErrorElement />,
      children: [
        {
          path: '',
          element: <Layout />,
          children: [
            {
              path: '',
              element: <Index />,
            },
            {
              path: 'voicemail',
              element: <VoicemailPage />,
            },
            {
              path: 'landscaper',
              element: <LandscaperPage />,
            },
            {
              path: 'demo/ai-voicemail',
              element: <AIVoicemailDemo />,
            },
            {
              path: 'about',
              element: <AboutPage />,
            },
            {
              path: 'about/max-kelley',
              element: <AboutMaxKelleyPage />,
            },
            {
              path: 'contact',
              element: <ContactPage />,
            },
            {
              path: 'thank-you',
              element: <ThankYouPage />,
            },
            {
              path: 'newsletter/confirmed',
              element: <NewsletterConfirmedPage />,
            },
            {
              path: 'search',
              element: <SearchPage />,
            },
            {
              path: 'blog',
              element: <BlogPage />,
            },
            {
              path: 'blog/:slug',
              element: <BlogPostPage />,
            },
            {
              path: 'services/agents',
              element: <AgentsPage />,
            },
            {
              path: 'services/ads',
              element: <AdsPage />,
            },
            {
              path: 'services/automation',
              element: <AutomationPage />,
            },
            {
              path: 'services/website',
              element: <WebsitePage />,
            },
            {
              path: 'services/google-ads-for-contractors',
              element: <GoogleAdsForContractorsPage />,
            },
            {
              path: 'review-stands',
              element: <ReviewStandsPage />,
            },
            {
              path: 'review-stands/thank-you',
              element: <ReviewStandsThankYouPage />,
            },
            {
              path: 'review-stands/setup',
              element: <ReviewStandsSetupPage />,
            },
            {
              path: 'team',
              element: <TeamPage />,
            },
            {
              path: 'authors/:slug',
              element: <AuthorPage />,
            },
            {
              path: 'case-studies',
              element: <CaseStudyIndex />,
            },
            {
              path: 'case-studies/:slug',
              element: <CaseStudyPage />,
            },
            {
              path: 'locations',
              element: <LocationsPage />,
            },
            {
              path: 'faq',
              element: <FaqPage />,
            },
            {
              path: 'industries',
              element: <IndustriesPage />,
            },
            {
              path: 'industries/:slug',
              element: <IndustryDetail />,
            },
            {
              path: 'locations/boston',
              element: <Navigate to="/locations" replace />,
            },
            {
              path: 'privacy-policy',
              element: <PrivacyPolicy />,
            },
            {
              path: 'terms-of-service',
              element: <TermsOfService />,
            },
            {
              path: 'tools/roi-calculator',
              element: <ROICalculator />,
            },
            {
              path: 'tools/site-audit',
              element: <SiteAudit />,
            },
            {
              path: 'developer/webhooks',
              element: <WebhooksDocumentationPage />,
            },
            {
              path: 'developer/api-reference',
              element: <ApiReferencePage />,
            },
            {
              path: 'webhooks-test',
              element: <Navigate to="/developer/webhooks" replace />,
            },
            {
              path: '*',
              element: <NotFound />,
            },
          ],
        },
        {
          path: 'web-design',
          element: (
            <Suspense fallback={<PageLoader />}>
              <WebDesignPage />
            </Suspense>
          ),
        },
        {
          path: 'intake/fourseasons',
          element: (
            <Suspense fallback={<PageLoader />}>
              <FourSeasonsIntake />
            </Suspense>
          ),
        },
        {
          path: 'intake/neconstructioned',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NEConstructionEdIntake />
            </Suspense>
          ),
        },
        {
          path: 'intake/mattfarah',
          element: (
            <Suspense fallback={<PageLoader />}>
              <MattFarahIntake />
            </Suspense>
          ),
        },
        {
          path: 'client-intake/matt-farah',
          element: (
            <Suspense fallback={<PageLoader />}>
              <MattFarahIntake />
            </Suspense>
          ),
        },
        {
          // Internal fulfilment. Sits beside /admin rather than inside the
          // marketing Layout: this is a packing screen, not a page, and the
          // navbar and footer are noise with a box open in front of you.
          path: '/ops',
          children: [
            {
              path: 'orders',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <OpsOrdersPage />
                </Suspense>
              ),
            },
            {
              path: 'orders/:orderNumber',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <OpsOrderPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          // Client request desk. Each client has /portal/<slug> + a PIN; Max's
          // inbox is /portal/admin behind the ops passcode. Outside the
          // marketing Layout for the same reason /ops is.
          path: '/portal',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PortalLandingPage />
                </Suspense>
              ),
            },
            {
              path: 'admin',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PortalAdminPage />
                </Suspense>
              ),
            },
            {
              path: ':slug',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PortalClientPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/admin',
          children: [
            {
              path: 'login',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AdminLoginPage />
                </Suspense>
              ),
            },
            {
              element: <AdminRoute />,
              children: [
                {
                  index: true,
                  element: <Navigate to="dashboard" replace />,
                },
                {
                  path: 'dashboard',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboardPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'finance',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <FinancePage />
                    </Suspense>
                  ),
                },
                {
                  path: 'blog/new',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <BlogPostForm />
                    </Suspense>
                  ),
                },
                {
                  path: 'blog/edit/:id',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <BlogPostForm />
                    </Suspense>
                  ),
                },
                {
                  path: 'leads',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <LeadInboxPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'testimonials',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <TestimonialsPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'activity-logs',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <ActivityLogsPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'notifications',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <NotificationsPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'seo-audit',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <SeoAuditPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'agent-health',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <AgentHealthPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'api-keys',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <ApiKeysPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'settings',
                  element: <Navigate to="/admin/api-keys" replace />,
                },
                {
                  path: 'blogs',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <BlogPostsPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'blog/categories',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <BlogCategoriesPage />
                    </Suspense>
                  ),
                },
                {
                  path: 'ai-writer',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <AIWriterPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: '/',
  }
);

// Wrapper component to track page views
const PageViewTracker = React.memo(
  ({ children }: { children: React.ReactNode }) => {
    const currentPathRef = React.useRef<string>(window.location.pathname);

    React.useEffect(() => {
      // Track initial page view only if different from last tracked
      const currentPath = window.location.pathname;
      if (currentPathRef.current !== currentPath) {
        trackPageView(currentPath);
        currentPathRef.current = currentPath;
      }

      // Set up listener for route changes
      const handleRouteChange = () => {
        const newPath = window.location.pathname;
        if (currentPathRef.current !== newPath) {
          trackPageView(newPath);
          currentPathRef.current = newPath;
        }
      };

      window.addEventListener('popstate', handleRouteChange);

      // Clean up
      return () => {
        window.removeEventListener('popstate', handleRouteChange);
      };
    }, []);

    return <>{children}</>;
  }
);

const App = () => (
  <HelmetProvider>
    {!isFinanceDocument && (
      <>
        <GoogleTagManager id={GTM_ID} />
        <GoogleTagManagerNoScript id={GTM_ID} />
        <MicrosoftClarity id={CLARITY_ID} />
        <MetaPixel id={META_PIXEL_ID} />
        <GoogleAdsConversion id={GOOGLE_ADS_ID} />
      </>
    )}
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <UserProvider>
          {isFinanceDocument ? (
            <RouterProvider router={router} />
          ) : (
            <PageViewTracker>
              <RouterProvider router={router} />
            </PageViewTracker>
          )}
        </UserProvider>
      </TooltipProvider>
    </QueryClientProvider>
    {!isFinanceDocument && <Analytics />}
  </HelmetProvider>
);

export default App;
